import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';

import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { companyActions } from '@nxsys/store/slices/company/reducer/companySlice';
import { GetBannerCompanyListParams } from '@nxsys/store/slices/company/types';
import { Icons } from '@nxsys/types/icons';
import { IBanner } from '@nxsys/types/models/banner';
import { ICompany } from '@nxsys/types/models/company';
import { formatStatus } from '@nxsys/utils';
import { Image } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CreateBannerModal from './components/CreateBannerModal';
import { BannerFormValues } from './components/BannerForm';
import {
  useCreateBanner,
  useDeleteBanner,
  useUpdateBanner,
} from '@nxsys/hooks/api/company';
import {
  CreateBannerParams,
  UpdateBannerParams,
} from '@nxsys/types/api/company';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { showMessage } from '@nxsys/utils/message';
import EditBannerModal from './components/EditBannerModal';
import { useActionGate } from '@nxsys/hooks/useCan';
import { roles } from '@nxsys/store/constants';

type Props = {
  data?: ICompany;
};

const BannerTab: React.FC<Props> = ({ data }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();

  const { bannerCompanyList } = useSelector(
    (state: AppState) => state.companyStore,
  );
  const { loading, list, pagination, queryParams } = bannerCompanyList;
  const { user } = useSelector((state: AppState) => state.appStore);
  // Banners are an admin's or the super admin's to manage, as the banner
  // service has always held.
  const canManageBanners = useActionGate(
    'settings:manageBanners',
    user?.role === roles.admin || user?.role === roles.supper_admin,
  );

  const [openCreateModal, setOpenCreateModal] = useState(false);

  const [selectedData, setSelectedData] = useState<IBanner | null>(null);
  const [openViewModal, setOpenViewModal] = useState(false);

  useEffect(() => {
    if (data?.id) {
      handleFetchData({
        page: 0,
        size: 20,
      });
    }
  }, [data?.id]);

  const handleFetchData = (params: GetBannerCompanyListParams) => {
    const newParams = { ...params, previewCompanyId: data?.id || '' };
    dispatch(companyActions.getBannerCompanyList(newParams));
  };

  const refetch = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const toggleViewModal = (record?: IBanner) => {
    setOpenViewModal(!openViewModal);
    setSelectedData(record || null);
  };

  const columns: ColumnsType<IBanner> = [
    {
      key: 'action',
      hidden: !canManageBanners,
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => toggleViewModal(record)}>
            <Icons.IconEdit />
          </li>
          <li onClick={() => handleDeleteBanner(record.id)}>
            <Icons.IconDelete />
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'isActive',
      title: messages['common.status'],
      dataIndex: 'isActive',
      render: (value) => {
        const status = value ? 'Active' : 'Inactive';
        return (
          <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
            {formatStatus(status)}
          </AppTagStatus>
        );
      },
    },
    {
      key: 'summary',
      title: messages['company.banner.summary'],
      dataIndex: 'summary',
      width: 300,
      render: (value) => <div className="truncate">{value || ''}</div>,
    },
    {
      key: 'mobileBackgroundImageUrl',
      title: messages['company.banner.backgroundImage'],
      dataIndex: 'mobileBackgroundImageUrl',
      width: 350,
      render: (value) =>
        value ? (
          <Image
            src={value}
            alt={messages['company.banner.backgroundImageAlt']}
            style={{ width: '320px', height: '114px' }}
          />
        ) : (
          <></>
        ),
    },
    {
      key: 'startAt',
      title: messages['company.banner.startAt'],
      dataIndex: 'startAt',
      render: (value) => (
        <span>{moment(value)?.tz('Europe/London')?.format('DD/MM/YYYY')}</span>
      ),
    },
    {
      key: 'endAt',
      title: messages['company.banner.endAt'],
      dataIndex: 'endAt',
      render: (value) => (
        <span>{moment(value)?.tz('Europe/London')?.format('DD/MM/YYYY')}</span>
      ),
    },
    {
      key: 'createdAt',
      title: messages['company.banner.createdAt'],
      dataIndex: 'createdAt',
      render: (value) => (
        <span>{moment(value)?.tz('Europe/London')?.format('DD/MM/YYYY')}</span>
      ),
    },
  ];

  const { run: createBanner } = useCreateBanner();
  const handleCreateBanner = async (values: BannerFormValues) => {
    if (!data?.id) return;

    const params: CreateBannerParams = {
      summary: values.summary,
      startAt: moment(values.startAt).toISOString(),
      endAt: moment(values.endAt).toISOString(),
      mobileBackgroundImageUrl: null,
      isActive: true,
      companyId: data.id,
    };

    const isUploadFile = !!(values.mobileBackgroundImageUrl as File)?.name;
    if (isUploadFile) {
      const res = await dispatch(
        commonActions.uploadFileToS3({
          file: values.mobileBackgroundImageUrl as File,
          purpose: 'banner',
        }),
      ).unwrap();
      params.mobileBackgroundImageUrl = res.url;
    }

    const res = await createBanner(params);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.addSuccess'],
      });
      setOpenCreateModal(false);
      refetch();
    }
  };

  const { run: updateBanner } = useUpdateBanner();
  const handleEditBanner = async (values: BannerFormValues) => {
    if (!selectedData?.id) return;

    const params: UpdateBannerParams = {
      summary: values.summary,
      startAt: moment(values.startAt).toISOString(),
      endAt: moment(values.endAt).toISOString(),
      isActive: values.isActive,
      mobileBackgroundImageUrl: values?.mobileBackgroundImageUrl as string,
    };

    const isUploadFile = !!(values.mobileBackgroundImageUrl as File)?.name;
    if (isUploadFile) {
      const res = await dispatch(
        commonActions.uploadFileToS3({
          file: values.mobileBackgroundImageUrl as File,
          purpose: 'banner',
        }),
      ).unwrap();
      params.mobileBackgroundImageUrl = res.url;
    }

    const res = await updateBanner(selectedData.id, params);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      setOpenViewModal(false);
      refetch();
    }
  };

  const { run: deleteBanner } = useDeleteBanner();
  const handleDeleteBanner = async (id: number) => {
    const res = await deleteBanner(id);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.deleteSuccess'],
      });
      refetch();
    }
  };

  return (
    <>
      <div className="p-[16px]">
        <div className="flex justify-between items-center mb-[16px]">
          <AppInputSearch onChange={handleSearchChange} />
          {canManageBanners && (
            <AppButton
              type="primary"
              name={messages['common.addNew']}
              leftIcon={<PlusOutlined />}
              onClick={() => setOpenCreateModal(true)}
            />
          )}
        </div>
        <AppsContent>
          <AppTableContainer
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1500, y: 700 }}
            titleEmpty={messages['common.noData']}
            columns={columns}
            onChangePagination={onChangePagination}
          />
        </AppsContent>
      </div>
      <CreateBannerModal
        isOpen={openCreateModal}
        toggle={() => setOpenCreateModal(false)}
        onSubmit={handleCreateBanner}
      />
      {openViewModal && selectedData && (
        <EditBannerModal
          isOpen={openViewModal}
          toggle={() => setOpenViewModal(false)}
          onSubmit={handleEditBanner}
          data={selectedData}
        />
      )}
    </>
  );
};

export default BannerTab;
